import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowDown, ArrowUp, ArrowUpDown, Edit } from 'lucide-react';
import { Material } from 'src/trpc';
import { cn } from '@/lib/utils';
import { MoneyFromCents } from '@libs/services/money';
import { MaterialStreamTypeEnum } from '@libs/schemas';

export const createMaterialColumns = (
  onEdit: (material: Material) => void,
): ColumnDef<Material & { containerInfo: Record<string, { name: string; size: string }> }>[] => [
  {
    accessorKey: 'name',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Name
          {!column.getIsSorted() && <ArrowUpDown className="ml-2 h-4 w-4" />}
          {column.getIsSorted() === 'asc' && <ArrowUp className="ml-2 h-4 w-4" />}
          {column.getIsSorted() === 'desc' && <ArrowDown className="ml-2 h-4 w-4" />}
        </Button>
      );
    },
    cell: ({ row }) => {
      return (
        <div className="font-medium max-w-[200px] leading-tight">
          <span className="text-wrap break-words">{row.getValue('name')}</span>
        </div>
      );
    },
  },
  {
    accessorKey: 'archived',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Status
          {!column.getIsSorted() && <ArrowUpDown className="ml-2 h-4 w-4" />}
          {column.getIsSorted() === 'asc' && <ArrowUp className="ml-2 h-4 w-4" />}
          {column.getIsSorted() === 'desc' && <ArrowDown className="ml-2 h-4 w-4" />}
        </Button>
      );
    },
    cell: ({ row }) => {
      const isArchived = row.getValue('archived') as boolean;
      return (
        <Badge
          variant={isArchived ? 'secondary' : 'default'}
          className={cn(!isArchived && 'bg-green-100 text-success')}
        >
          {isArchived ? 'Archived' : 'Active'}
        </Badge>
      );
    },
  },
  {
    accessorKey: 'streamType',
    header: 'Stream Type',
    cell: ({ row }) => {
      const streamType = row.getValue('streamType') as string;
      const isSingle = streamType === MaterialStreamTypeEnum.SINGLE;
      return (
        <Badge
          variant={isSingle ? 'default' : 'secondary'}
          className={cn(isSingle && 'bg-blue-600')}
        >
          {isSingle ? 'Single' : 'Mixed'}
        </Badge>
      );
    },
  },
  {
    accessorKey: 'isRestricted',
    header: 'Restrictions',
    cell: ({ row }) => {
      const isRestricted = row.getValue('isRestricted') as boolean;
      return (
        <Badge variant={isRestricted ? 'destructive' : 'outline'}>
          {isRestricted ? 'Restricted' : 'None'}
        </Badge>
      );
    },
  },
  {
    accessorKey: 'extraChargeInCents',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Extra Charge
          {!column.getIsSorted() && <ArrowUpDown className="ml-2 h-4 w-4" />}
          {column.getIsSorted() === 'asc' && <ArrowUp className="ml-2 h-4 w-4" />}
          {column.getIsSorted() === 'desc' && <ArrowDown className="ml-2 h-4 w-4" />}
        </Button>
      );
    },
    cell: ({ row }) => {
      const extraChargeInCents = row.getValue('extraChargeInCents') as number;
      return extraChargeInCents > 0 ? MoneyFromCents(extraChargeInCents).toFormat() : '$0.00';
    },
  },
  {
    accessorKey: 'recommendedContainerIds',
    header: 'Best For',
    cell: ({ row }) => {
      const bestForIds = row.getValue('recommendedContainerIds') as string[];
      const containerInfo = row.original.containerInfo as
        | Record<string, { name: string; size: string }>
        | undefined;

      if (!bestForIds || bestForIds.length === 0) {
        return (
          <div className="flex flex-row gap-1 justify-start">
            <span className=" text-muted">Not set</span>
          </div>
        );
      }

      return (
        <div className="flex flex-wrap gap-1 max-w-[250px]">
          {bestForIds.map((containerId) => (
            <Badge key={containerId} variant="default" className="bg-blue-600 text-xs">
              {containerInfo?.[containerId]?.name ||
                containerInfo?.[containerId]?.size ||
                containerId}
            </Badge>
          ))}
          {bestForIds.length > 3 && (
            <Badge variant="default" className="bg-blue-600 text-xs">
              +{bestForIds.length - 3}
            </Badge>
          )}
        </div>
      );
    },
  },
  {
    id: 'actions',
    header: 'Edit',
    cell: ({ row }) => {
      return (
        <Button
          size="sm"
          variant="outline"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(row.original);
          }}
        >
          <Edit className="h-4 w-4" />
        </Button>
      );
    },
  },
];
